import React, { useEffect, useState, useCallback } from "react";

import { RadioGroup, Radio, FormControl } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import NodeViewer from "./NodeViewer";
import ActionListEditor from "./ActionListEditor";
import LabReportGrid from "./LabReportGrid";
import { Divider } from "@mui/material";
import {
  Box,
  Alert,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTableOne,
  fetchTableTwo,
  fetchTableThree,
  setSelectedDate,
  setLabReport,
  fetchLabReport,
  setLabReportActions,
  saveLabReportActions,
  approveLabReport,
  unapproveLabReport,
} from "../reducers/reviewFrameSlice";

const COLORS = {
  approved: "#81c784",
  not_approved: "#ffb74d",
};

const BCOLORS = {
  approved: "#388e3c",
  not_approved: "#f57c00",
};

const COLOR_TITLES = {
  approved: "Approved",
  not_approved: "Not Approved",
};

const PIE_COLORS = [
  "#81c784",
  "#4db6ac",
  "#ffd54f",
  "#9575cd",
  "#7986cb",
  "#a1887f",
  "#f06292",
  "#64b5f6",
  "#e57373",
  "#ba68c8",
];

const TABLE_THREE_COLUMNS = [
  {
    field: "updated_at",
    headerName: "Date / Time",
    flex: 1.2,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "approved",
    headerName: "Approved",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "emis_id",
    headerName: "EMIS ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "lab_report_id",
    headerName: "Lab Report ID",
    flex: 1,
    align: "center",
    headerAlign: "center",
  },
  {
    field: "categories",
    headerName: "Categories",
    flex: 2,
    align: "left",
    headerAlign: "center",
    renderCell: (params) =>
      Array.isArray(params.value) ? params.value.join(", ") : "",
  },
];

const ReviewFrame = () => {
  const dispatch = useDispatch();
  const {
    liverun,
    tableOne,
    tableOneTotalProcessed,
    selectedDate,
    tableTwo,
    tableThree,
    selectedLabReport,
    loadingOne,
    loadingTwo,
    loadingThree,
    lab,
    labReportActions,
    loadingLabReport,
    savingActions,
    updatingApproval,
  } = useSelector((state) => state.reviewFrame);

  const license_key = useSelector(
    (state) => state.authen.loggedInUser?.license_key,
  );

  const has_write = useSelector(
    (state) => state.authen.loggedInUser?.license_role === "Change",
  );

  const [visibleKeys, setVisibleKeys] = useState({
    approved: false,
    not_approved: true,
  });

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [page, setPage] = useState(0);
  const [page3, setPage3] = useState(0);
  const [selectedChartType, setSelectedChartType] = useState("not_approved");

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const pageSize = 10;
  const totalPages = Math.ceil(tableTwo.length / pageSize);

  const clearAlerts = () => {
    setMessage(null);
    setError(null);
  };

  const paginatedData = tableTwo
    .filter((entry) => entry.approved > 0)
    .sort((a, b) => b.approved - a.approved)
    .slice(page * pageSize, (page + 1) * pageSize);

  const paginatedData3 = tableTwo
    .filter((entry) => entry.not_approved > 0)
    .sort((a, b) => b.not_approved - a.not_approved)
    .slice(page3 * pageSize, (page3 + 1) * pageSize);

  useEffect(() => {
    if (license_key) {
      dispatch(fetchTableOne({ liverun, license_key }));
    }
  }, [liverun, license_key, dispatch]);

  const handleActionsChange = useCallback(
    (newActions) => {
      dispatch(setLabReportActions(newActions));
    },
    [dispatch],
  );

  const handleSave = () => {
    clearAlerts();

    if (!selectedLabReport) {
      setError("No lab report selected.");
      return;
    }

    dispatch(
      saveLabReportActions({
        lab_report_id: selectedLabReport,
        actions: labReportActions,
        liverun,
        license_key,
      }),
    )
      .unwrap()
      .then(() => {
        setMessage("Actions saved successfully!");
      })
      .catch((error) => {
        setError(`Failed to save actions: ${error.message || error}`);
      });
  };

  const handleApprove = () => {
    clearAlerts();

    if (!selectedLabReport) {
      setError("No lab report selected.");
      return;
    }

    dispatch(
      approveLabReport({
        lab_report_id: selectedLabReport,
        liverun,
        license_key,
      }),
    )
      .unwrap()
      .then(() => {
        setMessage("Lab report approved successfully!");
      })
      .catch((error) => {
        setError(`Failed to approve: ${error.message || error}`);
      });
  };

  const handleUnapprove = () => {
    clearAlerts();

    if (!selectedLabReport) {
      setError("No lab report selected.");
      return;
    }

    dispatch(
      unapproveLabReport({
        lab_report_id: selectedLabReport,
        liverun,
        license_key,
      }),
    )
      .unwrap()
      .then(() => {
        setMessage("Lab report unapproved successfully!");
      })
      .catch((error) => {
        setError(`Failed to unapprove: ${error.message || error}`);
      });
  };

  useEffect(() => {
    if (tableOne.length > 0 && license_key && !selectedDate) {
      const firstDate = tableOne[0].date;
      const { start_date, end_date } = tableOne[0];
      dispatch(setSelectedDate(firstDate));
      dispatch(fetchTableTwo({ start_date, end_date, liverun, license_key }));
      dispatch(fetchTableThree({ start_date, end_date, liverun, license_key }));
      setPage(0);
      setPage3(0);
    }
  }, [tableOne, license_key, liverun, dispatch, selectedDate]);

  const handleChartClick = (data) => {
    const dateTitle = data?.activeLabel;
    if (!dateTitle) return;
    const matchedRecord = tableOne.find((entry) => entry.date === dateTitle);
    if (!matchedRecord) return;
    const { start_date, end_date } = matchedRecord;
    dispatch(setSelectedDate(dateTitle));
    dispatch(fetchTableTwo({ start_date, end_date, liverun, license_key }));
    dispatch(fetchTableThree({ start_date, end_date, liverun, license_key }));
    setPage(0);
    setPage3(0);
    setSelectedCategory(null);
  };

  const handlePieClick = (data) => {
    if (data && data.category) {
      setSelectedCategory(data.category);
    }
  };

  const handleLegendClick = (category) => {
    setSelectedCategory(category);
  };

  const filteredTableThree = selectedCategory
    ? tableThree.filter(
        (row) =>
          Array.isArray(row.categories) &&
          row.categories.includes(selectedCategory),
      )
    : tableThree;

  const chartFont = { fontSize: 11 };

  const pieData =
    selectedChartType === "approved" ? paginatedData : paginatedData3;

  const renderLegend = ({ payload }) => {
    const sorted = [...payload].sort(
      (a, b) => b.payload[selectedChartType] - a.payload[selectedChartType],
    );

    return (
      <ul style={{ listStyle: "none", padding: 0, margin: 0, fontSize: 12 }}>
        {sorted.map((entry, index) => (
          <li
            key={`item-${index}`}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 4,
              cursor: "pointer",
            }}
            onClick={() => handleLegendClick(entry.payload.category)}
          >
            <div
              style={{
                width: 12,
                height: 12,
                backgroundColor: entry.color,
                marginRight: 8,
              }}
            />
            <span
              style={{
                color: entry.color,
                fontWeight:
                  selectedCategory === entry.payload.category
                    ? "bold"
                    : "normal",
              }}
            >
              {entry.payload.category} : {entry.payload[selectedChartType]}
            </span>
          </li>
        ))}
      </ul>
    );
  };

  const handleRowSelect = (params) => {
    const lab_report_id = params.row?.lab_report_id;
    dispatch(setLabReport(lab_report_id));
    dispatch(fetchLabReport({ lab_report_id, liverun, license_key }));
  };

  return (
    <Box sx={{ mt: 1, pt: 0, pb: 0, px: 2 }}>
      <Paper elevation={2} sx={{ mt: 1, p: 2 }}>
        {message && (
          <Alert
            severity="success"
            onClose={() => setMessage(null)}
            sx={{ mb: 2 }}
          >
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
          {Object.entries(COLORS).map(([key, color]) => (
            <FormControlLabel
              key={key}
              control={
                <Switch
                  size="small"
                  checked={visibleKeys[key]}
                  onChange={() =>
                    setVisibleKeys((prev) => ({
                      ...prev,
                      [key]: !prev[key],
                    }))
                  }
                  sx={{
                    color,
                    "&.Mui-checked": { color },
                  }}
                />
              }
              label={
                <Typography variant="caption" sx={{ color }}>
                  {COLOR_TITLES[key]}
                </Typography>
              }
            />
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            alignItems: "flex-start",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle1">
                {tableOneTotalProcessed} Records Ready For Action - Select the
                week from the graph to take action
              </Typography>
            </Box>
            {loadingOne ? (
              <CircularProgress />
            ) : (
              <ResponsiveContainer width="100%" height={350}>
                <BarChart
                  data={tableOne}
                  margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
                  onClick={handleChartClick}
                >
                  <CartesianGrid stroke="#ccc" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    style={chartFont}
                    interval={0}
                    tick={{ fontSize: 12 }}
                    angle={-45}
                    textAnchor="end"
                    height={70}
                  />
                  <YAxis style={chartFont} />
                  <Tooltip contentStyle={chartFont} />
                  <Legend wrapperStyle={chartFont} />
                  {visibleKeys.approved && (
                    <Bar
                      dataKey="approved"
                      name="Approved"
                      stackId="a"
                      fill={COLORS.approved}
                      stroke={BCOLORS.approved}
                      strokeWidth={1}
                    >
                      <LabelList
                        dataKey="approved"
                        position="top"
                        fontSize={11}
                      />
                    </Bar>
                  )}
                  {visibleKeys.not_approved && (
                    <Bar
                      dataKey="not_approved"
                      name="Not Approved"
                      stackId="a"
                      fill={COLORS.not_approved}
                      stroke={BCOLORS.not_approved}
                      strokeWidth={1}
                    >
                      <LabelList
                        dataKey="not_approved"
                        position="top"
                        fontSize={11}
                      />
                    </Bar>
                  )}
                </BarChart>
              </ResponsiveContainer>
            )}
          </Box>

          {selectedDate && !loadingTwo && (
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    mr: 1,
                    whiteSpace: "nowrap",
                    minWidth: 100,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    fontWeight: "bold",
                  }}
                >
                  {selectedDate}
                </Typography>
                <FormControl component="fieldset" sx={{ ml: 0.5 }}>
                  <RadioGroup
                    row
                    value={selectedChartType}
                    onChange={(e) => {
                      setSelectedChartType(e.target.value);
                      setPage(0);
                    }}
                  >
                    <FormControlLabel
                      value="approved"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">Approved</Typography>
                      }
                    />
                    <FormControlLabel
                      value="not_approved"
                      control={
                        <Radio
                          sx={{ transform: "scale(0.8)", p: 0.5 }}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="caption">Not Approved</Typography>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <Tooltip contentStyle={chartFont} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="left"
                    content={renderLegend}
                    wrapperStyle={{
                      ...chartFont,
                      lineHeight: "24px",
                      position: "absolute",
                      left: 0,
                      top: 0,
                    }}
                  />
                  <Pie
                    data={pieData}
                    dataKey={selectedChartType}
                    nameKey="category"
                    outerRadius={100}
                    innerRadius={60}
                    paddingAngle={2}
                    label
                    stroke="#fff"
                    strokeWidth={1}
                    onClick={handlePieClick}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={PIE_COLORS[index % PIE_COLORS.length]}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>

              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    gap: 2,
                    mt: -5,
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "approved"
                        ? page === 0
                        : page3 === 0
                    }
                    onClick={() => {
                      selectedChartType === "approved"
                        ? setPage((p) => p - 1)
                        : setPage3((p) => p - 1);
                    }}
                  >
                    Previous
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={
                      selectedChartType === "approved"
                        ? page >= totalPages - 1
                        : page3 >= totalPages - 1
                    }
                    onClick={() => {
                      selectedChartType === "approved"
                        ? setPage((p) => p + 1)
                        : setPage3((p) => p + 1);
                    }}
                  >
                    Next
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", mt: 3 }}>
          {selectedDate && !loadingThree && (
            <Box sx={{ height: 300, overflow: "auto", width: "100%" }}>
              {selectedCategory && (
                <Box
                  sx={{
                    mb: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    Filtered by: {selectedCategory}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSelectedCategory(null)}
                  >
                    Clear Filter
                  </Button>
                </Box>
              )}

              <DataGrid
                key={
                  tableThree.length +
                  tableThree.filter((r) => r.approved).length
                }
                rowHeight={25}
                getRowId={(row) => row.lab_report_id}
                rows={filteredTableThree}
                columns={TABLE_THREE_COLUMNS}
                pagination={false}
                hideFooter={true}
                onRowClick={handleRowSelect}
                getRowClassName={(params) => {
                  if (params.row.approved) {
                    return params.indexRelativeToCurrentPage % 2 === 0
                      ? "approved-even"
                      : "approved-odd";
                  }
                  return params.indexRelativeToCurrentPage % 2 === 0
                    ? "even"
                    : "odd";
                }}
                sx={{
                  "& .MuiDataGrid-columnHeaders": {
                    marginBottom: "20px",
                  },
                  "& .even": {
                    backgroundColor: "#f9f9f9",
                  },
                  "& .odd": {
                    backgroundColor: "#ffffff",
                  },
                  "& .approved-even": {
                    backgroundColor: "#d9f0df",
                  },
                  "& .approved-odd": {
                    backgroundColor: "#e6f4ea",
                  },
                  "& .approved-odd:hover, & .approved-even:hover": {
                    backgroundColor: "#c8e6c9",
                  },

                  "& .approved-even.Mui-selected, & .approved-odd.Mui-selected":
                    {
                      backgroundColor: "#c8e6c9 !important",
                    },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "#e0e0e0 !important",
                  },
                }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      {!loadingThree && selectedLabReport && (
        <Box sx={{ mt: 4 }}>
          {loadingLabReport ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading lab report...</Typography>
            </Box>
          ) : lab && Object.keys(lab).length > 0 ? (
            <>
              <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Grid
                  container
                  spacing={2}
                  sx={{
                    mb: 2,
                    alignItems: "stretch",
                  }}
                >
                  {/* Column 1: Patient Data */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      pr: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Patient Data
                    </Typography>

                    <Typography variant="body2">
                      <strong>NHS Number:</strong> {lab.patient_data?.nhs_num}
                    </Typography>
                    <Typography variant="body2">
                      <strong>EMIS ID:</strong> {lab.patient_data?.emis_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Received Date:</strong>{" "}
                      {lab.patient_data?.received_date}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Issue Date:</strong>{" "}
                      {lab.patient_data?.issue_date}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Gender:</strong> {lab.patient_data?.gender}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Age:</strong> {lab.patient_data?.age}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Report ID:</strong>{" "}
                      {lab.patient_data?.lab_report_id}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Report Type:</strong>{" "}
                      {lab.patient_data?.report_type}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Approval Status:</strong>{" "}
                      <Box
                        component="span"
                        sx={{
                          color: lab.approved ? "#388e3c" : "#f57c00",
                          fontWeight: "bold",
                        }}
                      >
                        {lab.approved ? "Approved" : "Not Approved"}
                      </Box>
                    </Typography>
                  </Grid>

                  {/* Column 2: Patient Profile Summary */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      px: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Patient Profile Summary
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      {lab.patient_profile?.health_status &&
                        Object.entries(lab.patient_profile.health_status).map(
                          ([key, val]) => (
                            <Typography key={key} variant="body2">
                              <strong>{key}:</strong> {val?.value || "-"}{" "}
                              {val?.date ? `(${val.date})` : ""}
                            </Typography>
                          ),
                        )}
                    </Box>
                  </Grid>

                  {/* Column 3: Medications */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      px: 2,
                      borderRight: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Medications
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2">
                        <strong>Acute:</strong>{" "}
                        {lab.patient_profile?.medications?.acute?.length
                          ? lab.patient_profile.medications.acute.join(", ")
                          : "None"}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Repeat:</strong>{" "}
                        {lab.patient_profile?.medications?.repeat?.length
                          ? lab.patient_profile.medications.repeat.join(", ")
                          : "None"}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Column 4: Problems */}
                  <Grid
                    item
                    xs={12}
                    md={3}
                    sx={{
                      pl: 2,
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: "primary.main",
                        fontWeight: "bold",
                        mb: 1,
                        borderBottom: "2px solid",
                        borderColor: "primary.main",
                        display: "inline-block",
                        pb: 0.5,
                      }}
                    >
                      Problems
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        Active
                      </Typography>
                      {lab.patient_profile?.problems?.active &&
                      Object.keys(lab.patient_profile.problems.active).length >
                        0 ? (
                        Object.entries(lab.patient_profile.problems.active).map(
                          ([problem, date]) => (
                            <Typography key={problem} variant="body2">
                              {problem} {date ? `(${date})` : ""}
                            </Typography>
                          ),
                        )
                      ) : (
                        <Typography variant="body2">None</Typography>
                      )}

                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "bold", mt: 1 }}
                      >
                        Past
                      </Typography>
                      {lab.patient_profile?.problems?.past?.length ? (
                        lab.patient_profile.problems.past.map((p, i) => (
                          <Typography key={i} variant="body2">
                            {p}
                          </Typography>
                        ))
                      ) : (
                        <Typography variant="body2">None</Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
                <Divider sx={{ my: 2 }} />
                <LabReportGrid report={lab.patient_data?.report_data} />
                {Array.isArray(lab.categories) && lab.categories.length > 0 ? (
                  lab.categories.map((cat, idx) => (
                    <Paper
                      key={idx}
                      elevation={1}
                      sx={{
                        p: 2,
                        mb: 2,
                        borderLeft: "5px solid #1976d2",
                        backgroundColor: "#fafafa",
                      }}
                    >
                      <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                        {cat.category_name}
                      </Typography>
                      {cat.current_ruleset ? (
                        <Box sx={{ pl: 2 }}>
                          <NodeViewer
                            root={cat.current_ruleset}
                            nodeConfigs={{}}
                            onSelectNode={() => {}}
                            selectedNodeId={null}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No ruleset available.
                        </Typography>
                      )}
                    </Paper>
                  ))
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No categories found in this report.
                  </Typography>
                )}
              </Paper>

              <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                  <Alert severity="info">
                    Configuring actions for Lab Report ID: {selectedLabReport}
                  </Alert>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 2, display: "block" }}
                >
                  Current actions in state: {labReportActions?.length || 0}
                </Typography>

                <ActionListEditor
                  actions={labReportActions || []}
                  onChange={handleActionsChange}
                  readOnly={false}
                />

                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                    pointerEvents: has_write ? "auto" : "none",
                    opacity: has_write ? 1 : 0.6,
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={savingActions}
                    startIcon={
                      savingActions ? <CircularProgress size={16} /> : null
                    }
                  >
                    {savingActions ? "Saving..." : "Save Actions"}
                  </Button>
                </Box>
              </Paper>

              {/* Approval Action Buttons */}
              <Paper
                elevation={3}
                sx={{
                  p: 4,
                  mb: 4,
                  backgroundColor: lab.approved ? "#e8f5e9" : "#fff3e0",
                  border: `2px solid ${lab.approved ? "#388e3c" : "#f57c00"}`,
                  pointerEvents: has_write ? "auto" : "none",
                  opacity: has_write ? 1 : 0.2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: "bold",
                      color: lab.approved ? "#388e3c" : "#f57c00",
                    }}
                  >
                    Current Status: {lab.approved ? "Approved" : "Not Approved"}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      justifyContent: "center",
                      width: "100%",
                      maxWidth: 600,
                    }}
                  >
                    {!lab.approved ? (
                      <Button
                        variant="contained"
                        size="large"
                        color="success"
                        onClick={handleApprove}
                        disabled={updatingApproval}
                        startIcon={
                          updatingApproval ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <CheckCircleIcon />
                          )
                        }
                        sx={{
                          flex: 1,
                          py: 2,
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        {updatingApproval
                          ? "Processing..."
                          : "Move to Approved"}
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        size="large"
                        color="warning"
                        onClick={handleUnapprove}
                        disabled={updatingApproval}
                        startIcon={
                          updatingApproval ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <CancelIcon />
                          )
                        }
                        sx={{
                          flex: 1,
                          py: 2,
                          fontSize: "1.1rem",
                          fontWeight: "bold",
                          textTransform: "none",
                        }}
                      >
                        {updatingApproval
                          ? "Processing..."
                          : "Move to Not Approved"}
                      </Button>
                    )}
                  </Box>
                </Box>
              </Paper>
            </>
          ) : null}
        </Box>
      )}
    </Box>
  );
};

export default ReviewFrame;
